/**
 * Implement a class that represents a message in the thread.
 * - content - the content of the message
 * - json - the json content of the message (if any) - support a number of ways of finding it
 * - role - the role of the message (user, assistant, tool)
 * - toolCalls - the tool calls that were made in the message
 * - toolResults - the results of the tool calls
 * - attachments - array of attachments (documents, images, videos)
 * - cache: boolean - whether the message is cached
 */
export type MessageRole = "system" | "user" | "assistant" | "tool";

export type AttachmentType = "document" | "image" | "video";

export type DocumentFormat =
  | "pdf"
  | "csv"
  | "doc"
  | "docx"
  | "xls"
  | "xlsx"
  | "html"
  | "txt"
  | "md";
export type ImageFormat = "png" | "jpeg" | "gif" | "webp";
export type VideoFormat =
  | "mkv"
  | "mov"
  | "mp4"
  | "webm"
  | "flv"
  | "mpeg"
  | "mpg"
  | "wmv"
  | "three_gp";

export interface AttachmentSource {
  bytes?: Uint8Array;
  uri?: string;
  bucketOwner?: string;
}

export interface BaseAttachment {
  type: AttachmentType;
  source: AttachmentSource;
}

export interface DocumentAttachment extends BaseAttachment {
  type: "document";
  format: DocumentFormat;
  name: string;
}

export interface ImageAttachment extends BaseAttachment {
  type: "image";
  format: ImageFormat;
}

export interface VideoAttachment extends BaseAttachment {
  type: "video";
  format: VideoFormat;
}

export type Attachment = DocumentAttachment | ImageAttachment | VideoAttachment;

export interface ToolCall {
  name: string;
  toolUseId: string;
  arguments: Record<string, any>;
}

export interface Reasoning {
  text: string;
  type: "text";
  signature: string;
}

export interface ToolResult {
  name: string;
  toolUseId: string;
  result: any;
  error?: string;
}

export class Message {
  private _content?: string;
  private _role: MessageRole;
  private _reasoning: Reasoning[];
  private _toolCalls: ToolCall[];
  private _toolResults: ToolResult[];
  private _attachments: Attachment[];
  private _cache: boolean;

  constructor({
    content,
    role = "user",
    toolCalls = [],
    toolResults = [],
    attachments = [],
    reasoning = [],
    cache = false,
  }: {
    content?: string | undefined;
    role?: MessageRole;
    reasoning?: Reasoning[];
    toolCalls?: ToolCall[];
    toolResults?: ToolResult[];
    attachments?: Attachment[];
    cache?: boolean;
  }) {
    this._content = content;
    this._role = role;
    this._toolCalls = toolCalls;
    this._reasoning = reasoning;
    this._toolResults = toolResults;
    this._attachments = attachments;
    this._cache = cache;
  }

  get content(): string | undefined {
    return this._content;
  }

  get role(): MessageRole {
    return this._role;
  }

  get toolCalls(): ToolCall[] {
    return this._toolCalls;
  }

  get toolResults(): ToolResult[] {
    return this._toolResults;
  }

  get attachments(): Attachment[] {
    return this._attachments;
  }

  get cache(): boolean {
    return this._cache;
  }

  get reasoning(): Reasoning[] {
    return this._reasoning;
  }

  isToolResponse(): boolean {
    return this._toolResults.length > 0;
  }

  isAssistantMessage(): boolean {
    return this._role === "assistant";
  }

  isUserMessage(): boolean {
    return this._role === "user";
  }

  isSystemMessage(): boolean {
    return this._role === "system";
  }

  isToolCall(): boolean {
    return this._toolCalls.length > 0;
  }

  /**
   * Get attachments of a specific type
   */
  public getAttachmentsByType<T extends Attachment>(type: AttachmentType): T[] {
    return this._attachments.filter((a) => a.type === type) as T[];
  }

  /**
   * Add an attachment to the message
   */
  public addAttachment(attachment: Attachment): void {
    this._attachments.push(attachment);
  }

  /**
   * Remove an attachment from the message
   */
  public removeAttachment(index: number): void {
    if (index >= 0 && index < this._attachments.length) {
      this._attachments.splice(index, 1);
    }
  }

  /**
   * Attempts to parse and return JSON content from the message
   * Supports multiple formats:
   * 1. Direct JSON string
   * 2. JSON within markdown code blocks
   * 3. JSON within specific delimiters
   */
  get json(): any | null {
    try {
      // Try direct JSON parse
      if (this._content) {
        return JSON.parse(this._content);
      }
    } catch {
      try {
        // Try extracting from markdown code blocks
        if (this._content) {
          const codeBlockMatch = this._content.match(
            /```(?:json)?\s*([\s\S]*?)\s*```/
          );
          if (codeBlockMatch?.[1]) {
            return JSON.parse(codeBlockMatch[1]);
          }
        }
      } catch {
        try {
          // Try extracting from specific delimiters
          if (this._content) {
            const jsonMatch = this._content.match(/\{[\s\S]*\}/);
            if (jsonMatch) {
              return JSON.parse(jsonMatch[0]);
            }
          }
        } catch {
          // If all parsing attempts fail, return null
          return null;
        }
      }
    }
    return null;
  }
}
